import { Button, LoadingOverlay, Modal, Select, Text, Tooltip } from '@mantine/core'
import { modals } from '@mantine/modals'
import { useCallback, useEffect, useState } from 'react'
import { useMedplum } from '@medplum/react'
import type {
  Appointment,
  Coverage,
  CoverageEligibilityResponse,
  Encounter,
  Patient,
} from '@medplum/fhirtypes'
import { calculateAge, formatDateTime, formatPatientName } from '@/utils.ts'
import {
  IconAlertTriangle,
  IconCertificate,
  IconCheck,
  IconInfoCircle,
  IconX,
} from '@tabler/icons-react'
import { showNotification } from '@mantine/notifications'
import { ZOCDOC_APPOINTMENT_SYSTEM } from '@/model/middleware-types'
import { NoShowConfirmModal } from './NoShowConfirmModal'
import { notifyStatusChange, type StatusChangeResult } from './notifyStatusChange'
import { ZOCDOC_TRANSFER_BLOCKED } from './zocdocTransfer'
import { useMiddlewareClient } from '@/hooks'
import { useClient } from '@/hooks/useClient'
import { useIsManager } from '@/hooks/useIsManager'
import { useInsuranceVerdict, useInvalidateInsuranceVerdicts } from '@/hooks/useInsuranceVerdicts'
import { INSURANCE_STATUS_LABELS } from '@/utils/insuranceVerdict'
import { ImageZoomModal } from '@/components/ui/ImageZoomModal'
import type { InsuranceCardSide } from '@/types/insuranceTypes'
import { getResourceIdFromReference } from '@/utils/insuranceCardUtils'
import {
  convertFromISODate,
  getSubscriberDob,
  getSubscriberMemberId,
  getSubscriberName,
  normalizeResolvedCoverageSlots,
} from '@/utils/coverageUtils'
import { resolveCoverageInsuranceCardImageUrls } from '@/utils/documentReferenceUtils'
import { logError } from '@/utils/errorUtils'
import {
  CoverageEligibilityOutcomePanel,
  InsuranceCardGallery,
  InsuranceOrgDecisionPanel,
  VisitCoPayPanel,
} from '@/components/ui'
import { attachedCoverageReference, isSelfPayAppointment, readSelfPayStatus } from '@/utils/selfPay'
import { BADGE_TONE } from '@/components/ui/detailCardPrimitives'
import { TransferAppointmentForm } from './TransferAppointmentForm'
import { PhoneLink } from '@/components/ui/PhoneLink'

type Props = {
  onClose: () => void
  encounterId: string | null
  onStatusChange?: (appointmentId: string, status: string) => void
  onAppointmentRemoved?: (appointmentId: string) => void
  /** Fired after an in-modal change (insurance assigned/changed, eligibility
   *  decision) so the parent list can refresh derived columns without a reload. */
  onDetailsChanged?: () => void
}

type ResolvedCoveragesResult = {
  patientId: string
  mode: 'hg' | 'local-only'
  primary?: Coverage
  secondary?: Coverage
  tertiary?: Coverage
}

type CoverageSelect = {
  label: string
  value: string
  coverage: Coverage
}

const APPOINTMENT_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending Confirmation' },
  { value: 'booked', label: 'Confirmed' },
  { value: 'checked-in', label: 'Patient Ready' },
  { value: 'noshow', label: 'No Show' },
  { value: 'fulfilled', label: 'Completed' },
]

const APPOINTMENT_STATUS_COLORS: Record<string, string> = {
  pending: 'gold',
  booked: 'green',
  'checked-in': 'green',
  noshow: 'red',
  fulfilled: 'teal',
}

const APPOINTMENT_INSURANCE_LABELS = ['Primary', 'Secondary', 'Tertiary'] as const

export function AppointmentDetailsModal({
  encounterId,
  onClose,
  onStatusChange,
  onAppointmentRemoved,
  onDetailsChanged,
}: Props) {
  const medplum = useMedplum()
  const middleware = useMiddlewareClient()
  const orpcClient = useClient()
  const { isManager } = useIsManager()
  // Hand-written over the generated client, so the shapes have to be right by hand.
  //
  // `get` is declared with a `{ result }` wrapper because the coverages endpoint's own BODY
  // carries one — not because the client wraps anything. It does not: `call()` returns the parsed
  // body directly. `patch` therefore returns the response body as it stands, which is what the
  // status route's `UpdateAppointmentStatusResponseDto` describes.
  //
  // Declared rather than cast at the call site: an `as` there fights this type instead of
  // correcting it, and reads like a bug to anyone who trusts the declaration.
  const middlewareClient = middleware as unknown as {
    get: <T = unknown>(url: string) => Promise<{ result: T }>
    patch: <T = unknown>(url: string, body: unknown) => Promise<T>
  }

  const [patient, setPatient] = useState<Patient | null>(null)
  const [encounter, setEncounter] = useState<Encounter | null>(null)
  const [appointment, setAppointment] = useState<Appointment | null>(null)
  const [appointmentStatus, setAppointmentStatus] = useState<string | null>(null)
  /** Open while the no-show confirmation is waiting on the operator. */
  const [noShowPending, setNoShowPending] = useState(false)
  const zocdocAppointmentId = appointment?.identifier?.find(
    (entry) => entry.system === ZOCDOC_APPOINTMENT_SYSTEM,
  )?.value
  const isZocdocBound = Boolean(zocdocAppointmentId)
  const [isStatusUpdating, setIsStatusUpdating] = useState(false)
  const [consentSigned, setConsentSigned] = useState(false)
  const [coverage, setCoverage] = useState<Coverage | null>(null)
  const [coverages, setCoverages] = useState<CoverageSelect[]>([])
  const [coverageResponse, setCoverageResponse] = useState<CoverageEligibilityResponse | null>(null)
  // The visit's insurance verdict comes from the middleware (insuranceVerdicts); the eligibility
  // response only feeds the outcome panel's details.
  const { data: insuranceVerdict } = useInsuranceVerdict(
    appointment?.id ? { appointmentId: appointment.id } : null,
  )
  const invalidateInsuranceVerdicts = useInvalidateInsuranceVerdicts()
  const [insuranceCardPreviews, setInsuranceCardPreviews] = useState<
    Record<InsuranceCardSide, string | null>
  >({
    front: null,
    back: null,
  })
  const [insuranceCardPreviewLoading, setInsuranceCardPreviewLoading] = useState(false)
  const [isLoadingState, setIsLoadingState] = useState(true)
  const [zoomedImageUrl, setZoomedImageUrl] = useState<string | null>(null)
  const [isCoverageSelectorVisible, setIsCoverageSelectorVisible] = useState(false)
  const [isTransferExpanded, setIsTransferExpanded] = useState(false)
  const [callerOrgId, setCallerOrgId] = useState<string | null>(null)
  const [activeEncounterId, setActiveEncounterId] = useState<string | null>(encounterId)
  const [chainTargetOrgId, setChainTargetOrgId] = useState<string | null>(null)
  const [chainStatus, setChainStatus] = useState<'idle' | 'checking' | 'no-more' | 'error'>('idle')
  const [chainErrorMessage, setChainErrorMessage] = useState<string | null>(null)
  const COVERAGE_VERSION_TAG_SYSTEM = 'https://behuman.health/coverage-version-id'
  const coverageVersionId = coverage?.meta?.versionId
  const responseVersionId = coverageResponse?.meta?.tag?.find(
    (tag) => tag.system === COVERAGE_VERSION_TAG_SYSTEM,
  )?.code
  const isCoverageResponseStale = Boolean(
    coverageResponse && coverageVersionId && responseVersionId !== coverageVersionId,
  )
  const mapResolvedCoverages = useCallback((result: ResolvedCoveragesResult): CoverageSelect[] => {
    return normalizeResolvedCoverageSlots(result)
      .map((item, index) =>
        item?.id
          ? {
              label: `${APPOINTMENT_INSURANCE_LABELS[index]}: ${item.payor?.[0]?.display || 'Unknown Insurance'}`,
              value: item.id,
              coverage: item,
            }
          : null,
      )
      .filter(Boolean) as CoverageSelect[]
  }, [])

  const fetchLatestCoverageResponse = useCallback(
    async (patientId: string, selectedCoverage: Coverage | null) => {
      if (!selectedCoverage?.id) {
        setCoverageResponse(null)
        return
      }

      const patientReference = `Patient/${patientId}`
      const coverageReference = `Coverage/${selectedCoverage.id}`
      const requests = await medplum.searchResources(
        'CoverageEligibilityRequest',
        `patient=${patientReference}&_sort=-_lastUpdated&_count=100`,
      )
      const latestRequest = requests.find((request) =>
        request.insurance?.some((insurance) => insurance.coverage?.reference === coverageReference),
      )

      if (!latestRequest?.id) {
        setCoverageResponse(null)
        return
      }

      const latestResponse =
        (await medplum.searchOne(
          'CoverageEligibilityResponse',
          `request=CoverageEligibilityRequest/${latestRequest.id}&_sort=-_lastUpdated`,
        )) || null
      setCoverageResponse(latestResponse)
    },
    [medplum],
  )

  const loadInsuranceCards = useCallback(
    async (selectedCoverage: Coverage | null) => {
      if (!selectedCoverage) {
        setInsuranceCardPreviews({ front: null, back: null })
        return
      }

      setInsuranceCardPreviewLoading(true)
      try {
        setInsuranceCardPreviews(
          await resolveCoverageInsuranceCardImageUrls(medplum, selectedCoverage),
        )
      } finally {
        setInsuranceCardPreviewLoading(false)
      }
    },
    [medplum],
  )

  const loadState = useCallback(async () => {
    if (!activeEncounterId) {
      return
    }

    setIsLoadingState(true)
    try {
      const nextEncounter = await medplum.readResource('Encounter', activeEncounterId)
      setEncounter(nextEncounter)

      const appointmentId = nextEncounter.appointment?.[0]?.reference?.split('/')[1]
      const nextAppointment = appointmentId
        ? ((await medplum.readResource('Appointment', appointmentId)) as Appointment)
        : null
      setAppointment(nextAppointment)
      setAppointmentStatus(nextAppointment?.status ?? null)

      if (!nextEncounter.subject) {
        return
      }

      const nextPatient = (await medplum.readReference(nextEncounter.subject)) as Patient
      setPatient(nextPatient)

      const { result } = await middlewareClient.get<ResolvedCoveragesResult>(
        `/insurance/patient/${nextPatient.id}/coverages`,
      )
      const nextCoverages = mapResolvedCoverages(result)
      setCoverages(nextCoverages)

      const practRole = await medplum.searchOne(
        'PractitionerRole',
        `practitioner=${nextEncounter.participant?.[0]?.individual?.reference}`,
      )

      if (practRole?.organization) {
        const organizationId = practRole.organization.reference?.split('/').pop()

        if (organizationId && nextPatient.id) {
          try {
            const consentResult = await orpcClient.consentGenerate({
              patientId: nextPatient.id,
              organizationId,
            })
            setConsentSigned(Boolean(consentResult.isSigned))
          } catch (error) {
            logError('appointments', 'Check appointment consent', error)
            setConsentSigned(false)
          }
        }
      }

      const appointmentCoverageReference = nextAppointment
        ? attachedCoverageReference(nextAppointment)
        : undefined

      let nextCoverage: Coverage | null = null
      if (appointmentCoverageReference) {
        const attachedCoverageId = getResourceIdFromReference(appointmentCoverageReference)
        nextCoverage =
          nextCoverages.find((item) => item.coverage.id === attachedCoverageId)?.coverage ||
          ((await medplum.readReference({ reference: appointmentCoverageReference })) as Coverage)
      }

      setCoverage(nextCoverage)
      await Promise.all([
        fetchLatestCoverageResponse(nextPatient.id as string, nextCoverage),
        loadInsuranceCards(nextCoverage),
      ])
    } catch (error) {
      logError('appointments', 'Load appointment details', error)
    } finally {
      setIsLoadingState(false)
    }
  }, [
    activeEncounterId,
    fetchLatestCoverageResponse,
    loadInsuranceCards,
    mapResolvedCoverages,
    medplum,
    middlewareClient,
    orpcClient,
  ])

  // Sync prop -> internal active encounter when parent opens/closes the modal.
  // When parent passes a new encounterId we reset chain state.
  useEffect(() => {
    setActiveEncounterId(encounterId)
    if (encounterId === null) {
      setChainTargetOrgId(null)
      setIsTransferExpanded(false)
      setChainStatus('idle')
      setChainErrorMessage(null)
    }
  }, [encounterId])

  useEffect(() => {
    if (!activeEncounterId) {
      return
    }

    void loadState()

    return () => {
      setPatient(null)
      setEncounter(null)
      setAppointment(null)
      setCoverage(null)
      setCoverages([])
      setCoverageResponse(null)
      setInsuranceCardPreviews({ front: null, back: null })
      setConsentSigned(false)
      setAppointmentStatus(null)
      setIsCoverageSelectorVisible(false)
      setIsLoadingState(true)
    }
  }, [activeEncounterId, loadState])

  // Auto-expand transfer form when we arrived from a chain step.
  useEffect(() => {
    if (activeEncounterId && chainTargetOrgId) {
      setIsTransferExpanded(true)
    }
  }, [activeEncounterId, chainTargetOrgId])

  useEffect(() => {
    let active = true
    const profile = medplum.getProfile()
    if (!profile?.id) {
      setCallerOrgId(null)
      return
    }
    medplum
      .searchOne('PractitionerRole', `practitioner=Practitioner/${profile.id}`)
      .then((role) => {
        if (!active) return
        const orgRef = role?.organization?.reference
        const id = orgRef?.split('/')[1] ?? null
        setCallerOrgId(id)
      })
      .catch(() => {
        if (active) setCallerOrgId(null)
      })
    return () => {
      active = false
    }
  }, [medplum])

  const applyAppointmentStatus = async (status: string, pushToZocdoc = false) => {
    if (!appointment?.id) return

    try {
      setIsStatusUpdating(true)
      const result = await middlewareClient.patch<StatusChangeResult>(
        `/appointment/${appointment.id}/status`,
        {
          status,
          ...(pushToZocdoc && { pushToZocdoc: true }),
        },
      )
      setAppointmentStatus(status)
      notifyStatusChange(result)
      onStatusChange?.(appointment.id, status)
      // Following the patient's Zocdoc reschedule moved the visit, so the times on screen are old.
      if (result?.zocdocMovedTo) onDetailsChanged?.()
    } catch {
      showNotification({ color: 'red', message: 'Failed to update appointment status' })
    } finally {
      setIsStatusUpdating(false)
    }
  }

  const handleAppointmentStatusChange = async (status: string | null) => {
    if (!status || !appointment?.id || status === appointmentStatus) {
      return
    }

    // No-show is the one value on this dropdown that reaches outside beHuman, so it asks first
    // rather than firing on the select. Every other status writes straight through, as before.
    // **Only a Zocdoc-bound visit asks.** A no-show on an ordinary beHuman appointment writes
    // straight through, exactly as it did before this integration — the confirmation exists to
    // offer the Zocdoc half, and there is none to offer here.
    if (status === 'noshow' && isZocdocBound) {
      setNoShowPending(true)
      return
    }

    await applyAppointmentStatus(status)
  }

  const handleAppointmentCoverageChange = async (coverageId: string | null) => {
    setIsCoverageSelectorVisible(false)

    if (!appointment || !patient?.id) {
      showNotification({ color: 'red', message: 'Appointment not found' })
      return
    }

    const selectedCoverage = coverages.find((item) => item.value === coverageId)?.coverage || null

    try {
      const supportingInformation = [
        ...(appointment.supportingInformation ?? []).filter(
          (reference) => !reference.reference?.startsWith('Coverage/'),
        ),
        ...(selectedCoverage?.id ? [{ reference: `Coverage/${selectedCoverage.id}` }] : []),
      ]

      const updatedAppointment = await medplum.updateResource<Appointment>({
        ...appointment,
        supportingInformation: supportingInformation.length ? supportingInformation : undefined,
      })

      setAppointment(updatedAppointment)
      setCoverage(selectedCoverage)
      await Promise.all([
        fetchLatestCoverageResponse(patient.id, selectedCoverage),
        loadInsuranceCards(selectedCoverage),
      ])

      showNotification({
        color: 'green',
        message: selectedCoverage
          ? 'Appointment insurance updated.'
          : 'Appointment insurance removed.',
      })
      void invalidateInsuranceVerdicts()
      onDetailsChanged?.()
    } catch (error) {
      showNotification({
        color: 'red',
        message: (error as Error).message || 'Failed to update appointment insurance.',
      })
    }
  }

  const handleCloseInternal = () => {
    setActiveEncounterId(null)
    setChainTargetOrgId(null)
    setIsTransferExpanded(false)
    setChainStatus('idle')
    setChainErrorMessage(null)
    onClose()
  }

  const handleTransferred = async (info: {
    sourceAppointmentId: string
    targetOrgId: string
    patientId: string
  }) => {
    setIsTransferExpanded(false)
    setChainStatus('checking')
    setChainErrorMessage(null)
    // Optimistic UI: tell parent the source appointment is gone so it can
    // drop it from its local list/calendar state without a refetch.
    onAppointmentRemoved?.(info.sourceAppointmentId)
    try {
      const future = await orpcClient.getFutureAppointmentsByPatient({
        patientId: info.patientId,
        excludeAppointmentId: info.sourceAppointmentId,
      })
      const next = future.items.find((item) => item.encounterId)
      if (next?.encounterId) {
        const nextEncounterId = next.encounterId
        const dateLabel = next.start
          ? new Date(next.start).toLocaleString(undefined, {
              dateStyle: 'medium',
              timeStyle: 'short',
            })
          : 'an unspecified time'
        const providerLabel = next.practitionerName || 'a practitioner'
        const remaining = future.items.filter((item) => item.encounterId).length

        modals.openConfirmModal({
          title: 'Patient has another appointment',
          children: (
            <Text size="sm">
              This patient has {remaining} more future appointment(s) in your organization. The next
              one is on <strong>{dateLabel}</strong> with <strong>{providerLabel}</strong>. Would
              you like to transfer it as well?
            </Text>
          ),
          labels: { confirm: 'Yes, transfer next', cancel: 'No, finish here' },
          onConfirm: () => {
            setChainTargetOrgId(info.targetOrgId)
            setActiveEncounterId(nextEncounterId)
            setChainStatus('idle')
          },
          onCancel: () => {
            setChainStatus('no-more')
          },
        })
      } else {
        setChainStatus('no-more')
      }
    } catch (error) {
      console.error('[transfer-chain] future lookup failed:', error)
      logError('appointment-transfer-chain', 'lookup failed', error)
      setChainStatus('error')
      setChainErrorMessage(
        error instanceof Error ? error.message : 'Unknown error checking next appointments',
      )
    }
  }

  const isChainBusy = chainStatus === 'checking'

  return (
    <Modal
      size="70%"
      opened={!!activeEncounterId}
      onClose={handleCloseInternal}
      closeOnClickOutside={!isChainBusy}
      closeOnEscape={!isChainBusy}
    >
      <LoadingOverlay visible={isLoadingState} />

      <div>
        {chainStatus === 'checking' && (
          <div className="mb-4 rounded-md border border-blue-300 bg-blue-50 p-3 text-blue-900">
            Checking for other future appointments of this patient…
          </div>
        )}
        {chainStatus === 'no-more' && (
          <div className="mb-4 rounded-md border border-green-300 bg-green-50 p-3 text-green-900">
            <strong>Transfer chain complete.</strong> No more future appointments for this patient.
            You can close this dialog.
          </div>
        )}
        {chainStatus === 'error' && (
          <div className="mb-4 rounded-md border border-yellow-300 bg-yellow-50 p-3 text-yellow-900">
            <strong>Transfer succeeded</strong>, but we could not check for additional future
            appointments. {chainErrorMessage}
          </div>
        )}

        {patient && (
          <div>
            <div className="text-base text-lg font-bold">
              {formatPatientName(patient.name?.[0])}, {calculateAge(patient.birthDate || '')}
            </div>
            <div className="text-light text-sm">
              {patient.gender?.charAt(0).toUpperCase()}
              {patient.gender?.slice(1)},{' '}
              {formatDateTime(patient.birthDate || '', '/', false, true)}
            </div>
            {(appointment?.reasonCode?.[0]?.text || encounter?.reasonCode?.[0]?.text) && (
              <div className="mt-1 text-sm font-medium text-[#C4573E]">
                Reason for visit:{' '}
                {appointment?.reasonCode?.[0]?.text || encounter?.reasonCode?.[0]?.text}
              </div>
            )}
          </div>
        )}

        {patient && appointment && callerOrgId && (
          <div className="mt-4">
            {!isTransferExpanded || isZocdocBound ? (
              <Tooltip
                label={ZOCDOC_TRANSFER_BLOCKED}
                disabled={!isZocdocBound}
                withArrow
                multiline
                w={260}
              >
                {/* A disabled button fires no mouse events, so the tooltip hangs off this wrapper. */}
                <span className="inline-block">
                  <Button
                    variant="outline"
                    disabled={isZocdocBound}
                    onClick={() => setIsTransferExpanded(true)}
                  >
                    Transfer to another organization
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <TransferAppointmentForm
                appointment={appointment}
                patient={patient}
                callerOrgId={callerOrgId}
                initialTargetOrgId={chainTargetOrgId ?? undefined}
                onClose={() => setIsTransferExpanded(false)}
                onTransferred={handleTransferred}
              />
            )}
          </div>
        )}

        <div className="my-4 h-[1px] w-full bg-gray-200" />

        <div className="flex items-start gap-3">
          {patient && (
            <div className="flex-1 rounded-lg border-1 border-gray-200 p-2">
              <div className="flex gap-2">
                <IconCertificate /> Patient Information
              </div>
              <div className="mt-2">
                <p>
                  Phone:{' '}
                  <PhoneLink
                    phone={patient.telecom?.find((item) => item.system === 'phone')?.value}
                    fallback=""
                  />
                </p>
                <p>
                  Email: {patient.telecom?.find((item) => item.system === 'email')?.value || ''}
                </p>
                <div className="space-y-3">
                  <div>
                    Attached Insurance:{' '}
                    {coverage ? (
                      <ul className="list-disc pl-5">
                        <li>
                          <b>name:</b> {coverage.payor?.[0]?.display || '-'}
                        </li>
                        <li>
                          <b>type:</b> {coverage.relationship?.coding?.[0]?.display || '-'}
                        </li>
                        <li>
                          <b>payer ID:</b> {coverage.payor?.[0]?.identifier?.value || '-'}
                        </li>
                        <li>
                          <b>member ID:</b> {coverage.subscriberId || '-'}
                        </li>
                        {coverage.relationship?.coding?.[0]?.code?.toLowerCase() !== 'self' && (
                          <>
                            <li>
                              <b>subscriber name:</b> {getSubscriberName(coverage) || '-'}
                            </li>
                            <li>
                              <b>subscriber DOB:</b>{' '}
                              {convertFromISODate(getSubscriberDob(coverage)) || '-'}
                            </li>
                            <li>
                              <b>subscriber member id:</b> {getSubscriberMemberId(coverage) || '-'}
                            </li>
                          </>
                        )}
                      </ul>
                    ) : appointment && isSelfPayAppointment(appointment) ? (
                      <b>Self Pay</b>
                    ) : (
                      <b>not provided</b>
                    )}
                  </div>

                  {!(appointment && isSelfPayAppointment(appointment)) && (
                    <>
                      {!isCoverageSelectorVisible ? (
                        <Button
                          variant="outline"
                          onClick={() => setIsCoverageSelectorVisible(true)}
                        >
                          {coverage ? 'Change' : 'Assign'} insurance
                        </Button>
                      ) : (
                        <div className="max-w-md">
                          <Select
                            autoFocus
                            clearable
                            data={coverages.map((item) => ({
                              label: item.label,
                              value: item.value,
                            }))}
                            value={coverage?.id || null}
                            placeholder={
                              coverages.length
                                ? 'Select appointment insurance'
                                : 'No ranked insurance available'
                            }
                            onChange={(value) => void handleAppointmentCoverageChange(value)}
                          />
                        </div>
                      )}

                      {coverage && (
                        <div className="mt-2">
                          <InsuranceCardGallery
                            previews={insuranceCardPreviews}
                            loading={insuranceCardPreviewLoading}
                            helperText="Images attached to the selected insurance."
                            onImageClick={(url) => setZoomedImageUrl(url)}
                          />
                        </div>
                      )}
                    </>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex-1 rounded-lg border-1 border-gray-200 p-2">
            <p className="flex gap-1">
              Consent:{' '}
              {consentSigned ? (
                <>
                  Yes
                  <IconCheck color="green" />
                </>
              ) : (
                <>
                  No
                  <IconX color="red" />
                </>
              )}
            </p>

            <div className="my-1 h-[1px] w-full bg-gray-200" />

            <div>Appt Date: {formatDateTime(encounter?.period?.start || '', '-', true)}</div>

            <div className="my-1 h-[1px] w-full bg-gray-200" />

            <div className="flex items-center gap-2">
              <span className="shrink-0">Status:</span>
              <Select
                size="xs"
                data={APPOINTMENT_STATUS_OPTIONS.filter(({ value }) => {
                  if (value === appointmentStatus) return true
                  if (value === 'checked-in' && !isManager) return false
                  // No-show is offered to every practitioner, on a Zocdoc-bound visit as much as
                  // any other: recording their own patient absent is the practitioner's own call,
                  // and the server takes it. Only the optional Zocdoc half carries the
                  // coordinator's privilege, and the confirmation below is where that is said.
                  return true
                })}
                value={appointmentStatus}
                onChange={handleAppointmentStatusChange}
                disabled={isStatusUpdating || !appointment?.id}
                leftSection={
                  appointmentStatus ? (
                    <span
                      className="inline-block h-2 w-2 rounded-full"
                      style={{
                        backgroundColor: APPOINTMENT_STATUS_COLORS[appointmentStatus] ?? 'gray',
                      }}
                    />
                  ) : null
                }
                styles={{ input: { minWidth: 160 } }}
              />
              {appointment &&
                isSelfPayAppointment(appointment) &&
                readSelfPayStatus(appointment) === 'unpaid' && (
                  <span
                    className={`w-fit rounded-md px-2 py-[3px] text-[11px] font-bold ${BADGE_TONE.coral}`}
                  >
                    Unpaid
                  </span>
                )}
            </div>

            <div className="my-1 h-[1px] w-full bg-gray-200" />

            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <span className="shrink-0">Insurance status:</span>
                {appointment && isSelfPayAppointment(appointment) ? (
                  <span>Self Pay · No insurance used for this visit</span>
                ) : (
                  <div className="flex items-center gap-1">
                    {insuranceVerdict
                      ? INSURANCE_STATUS_LABELS[insuranceVerdict.insuranceStatus]
                      : '—'}
                    {insuranceVerdict?.insuranceStatus === 'verified' ? (
                      <IconCheck color="green" />
                    ) : insuranceVerdict?.insuranceStatus === 'review-required' ? (
                      <IconAlertTriangle color="orange" />
                    ) : (
                      <IconInfoCircle color="gray" />
                    )}
                  </div>
                )}
              </div>
            </div>

            {!(appointment && isSelfPayAppointment(appointment)) && (
              <>
                <div className="mt-4">
                  <InsuranceOrgDecisionPanel
                    patientId={patient?.id || ''}
                    coverage={coverage}
                    encounterId={activeEncounterId || undefined}
                    onSaved={() => {
                      void loadState()
                      onDetailsChanged?.()
                    }}
                  />
                </div>

                <CoverageEligibilityOutcomePanel
                  latestEligibilityResponse={coverageResponse}
                  isEligibilityStale={isCoverageResponseStale}
                  className="mt-4 max-h-100 overflow-y-auto rounded-2xl border border-green-200 bg-green-50 p-4"
                />
              </>
            )}

            <div className="mt-4">
              <VisitCoPayPanel
                encounterId={activeEncounterId}
                selfPay={appointment ? isSelfPayAppointment(appointment) : false}
              />
            </div>
          </div>
        </div>
      </div>

      <ImageZoomModal
        opened={!!zoomedImageUrl}
        imageUrl={zoomedImageUrl}
        title="Insurance Card Preview"
        imageAlt="Insurance card"
        onClose={() => setZoomedImageUrl(null)}
      />

      <NoShowConfirmModal
        opened={noShowPending}
        zocdocAppointmentId={zocdocAppointmentId}
        startAt={appointment?.start}
        saving={isStatusUpdating}
        onClose={() => setNoShowPending(false)}
        onConfirm={({ pushToZocdoc }) => {
          setNoShowPending(false)
          void applyAppointmentStatus('noshow', pushToZocdoc)
        }}
      />
    </Modal>
  )
}
